import { type AxialCoord, hexDistance } from "./hex";

/**
 * Choosing among candidates, the core's tie-break contract in one place: every "best", "nearest"
 * or "ranked" choice keeps the FIRST candidate on a tie, so the input's order (map order for
 * hexes, id order for units) is the last key and a replay chooses the same. A rule that wants
 * another tie-break says so with an explicit key.
 */

/** The candidate with the highest score; the first wins a tie. Undefined for no candidates. */
export function bestBy<Item>(
  items: Iterable<Item>,
  score: (item: Item) => number,
): Item | undefined {
  let best: Item | undefined;
  let bestScore = 0;
  let found = false;
  for (const item of items) {
    const value = score(item);
    if (!found || value > bestScore) {
      best = item;
      bestScore = value;
      found = true;
    }
  }
  return best;
}

/**
 * The candidate nearest `from` by hex distance that `accepts` takes; the first wins a tie.
 * `accepts` is asked only of a candidate nearer than the best so far, so a costly test runs as
 * rarely as the hand-written loop it replaces.
 */
export function nearestTo<Item extends { readonly coord: AxialCoord }>(
  items: Iterable<Item>,
  from: AxialCoord,
  accepts: (item: Item) => boolean = () => true,
): Item | undefined {
  let best: Item | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const item of items) {
    const distance = hexDistance(item.coord, from);
    if (distance < bestDistance && accepts(item)) {
      best = item;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The candidates ordered by `keys`, lowest first, each key breaking the previous one's ties and
 * the input order breaking the last. Each key is read once per candidate. A descending key is
 * written negated: `(city) => -city.score`.
 */
export function rankBy<Item>(
  items: readonly Item[],
  ...keys: readonly ((item: Item) => number)[]
): Item[] {
  return items
    .map((item, order) => ({ item, order, values: keys.map((key) => key(item)) }))
    .sort((a, b) => {
      for (const [index, value] of a.values.entries()) {
        // Truthiness, as the `a - b || c - d` chains it replaces: a zero difference falls through.
        const difference = value - (b.values[index] ?? 0);
        if (difference) {
          return difference;
        }
      }
      return a.order - b.order;
    })
    .map(({ item }) => item);
}

/**
 * The first candidate whose attempt changes the state, with the state it makes: a rule returns
 * the same object when it refuses, so "changed" is "accepted". Undefined when none is.
 */
export function firstAccepted<Candidate, State>(
  state: State,
  candidates: Iterable<Candidate>,
  attempt: (state: State, candidate: Candidate) => State,
): { readonly chosen: Candidate; readonly next: State } | undefined {
  for (const candidate of candidates) {
    const next = attempt(state, candidate);
    if (next !== state) {
      return { chosen: candidate, next };
    }
  }
  return undefined;
}

/** The state after the first candidate the rule accepts (`firstAccepted`); the same object when none is. */
export function applyFirst<Candidate, State>(
  state: State,
  candidates: Iterable<Candidate>,
  attempt: (state: State, candidate: Candidate) => State,
): State {
  return firstAccepted(state, candidates, attempt)?.next ?? state;
}
